<template>
  <div class="page-container bg-surface-50 dark:bg-surface-950">
    <Navbar />

    <main class="page-content">
      <!-- Loading State -->
      <div v-if="!userProfile" class="flex items-center justify-center py-12">
        <LoadingSpinner message="Loading transaction data..." />
      </div>

      <!-- Main Content -->
      <div v-else class="space-y-6">
        <PortalAdminCustomerPicker v-if="isPortalSuperAdmin" :switchable="false" class="mb-6" />

        <!-- Header -->
        <div class="page-header animate-fade-up">
          <button
            @click="$router.back()"
            class="inline-flex items-center text-accent-500 hover:text-accent-400 mb-3 transition-all duration-200 hover:-translate-x-0.5 font-medium group text-sm"
          >
            <ArrowLeftIcon class="h-4 w-4 mr-1.5 transition-transform duration-200 group-hover:-translate-x-1" />
            Back to Dashboard
          </button>
          <div class="flex items-start justify-between">
            <div>
              <h1 class="text-display-sm text-surface-900 dark:text-surface-50 mb-1">
                Transaction History
              </h1>
              <p class="text-surface-500 dark:text-surface-400 text-sm">
                All {{ userProfile?.group ? 'vessel' : 'asset' }} fueling transactions for the selected period
              </p>
            </div>
          </div>
        </div>

        <!-- Filters -->
        <div class="animate-fade-up" style="animation-delay: 40ms">
          <BaseCard class="sticky top-4 z-10 border border-surface-200/60 dark:border-surface-700/60 backdrop-blur-sm bg-white/80 dark:bg-surface-900/80 rounded-2xl">
            <template #header>
              <div class="flex items-center gap-3">
                <div class="p-1.5 rounded-lg bg-accent-500/10">
                  <AdjustmentsHorizontalIcon class="h-5 w-5 text-accent-500" />
                </div>
                <div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-100">Filters &amp; Date Range</h3>
                  <p class="text-[10px] text-surface-400 dark:text-surface-500">Refine your transaction view</p>
                </div>
              </div>
            </template>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <DateRangePicker v-model="dateRange" :min="minDate" :max="maxDate" />
              <EntityFilter
                v-model="selectedEntities"
                :options="allEntities"
                :label="`${userProfile?.group ? 'Vessel' : 'Asset'} Filter`"
                :placeholder="`Select ${userProfile?.group ? 'Vessels' : 'Assets'}`"
              />
              <EntityFilter
                v-model="selectedSites"
                :options="allSites"
                label="Site Filter"
                placeholder="Select Sites"
              />
            </div>
          </BaseCard>
        </div>

        <!-- KPI Stats -->
        <div class="animate-fade-up" style="animation-delay: 80ms">
          <div class="flex items-center gap-2 mb-4">
            <div class="h-px flex-1 bg-gradient-to-r from-accent-500/40 to-transparent"></div>
            <span class="text-xs font-bold uppercase tracking-[0.2em] text-accent-600 dark:text-accent-400">Summary</span>
            <div class="h-px flex-1 bg-gradient-to-l from-accent-500/40 to-transparent"></div>
          </div>
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div
              v-for="(stat, index) in quickStats"
              :key="index"
              class="kpi-card group"
              :style="{ animationDelay: `${80 + index * 50}ms` }"
            >
              <div class="absolute left-0 top-0 bottom-0 w-1 rounded-full" :style="{ backgroundColor: stat.color }"></div>
              <div class="flex items-center justify-between pl-3">
                <div class="flex-1 min-w-0">
                  <p class="text-[10px] font-bold text-surface-400 dark:text-surface-500 uppercase tracking-[0.12em] mb-1.5 leading-tight">
                    {{ stat.label }}
                  </p>
                  <p class="text-xl font-extrabold font-mono text-surface-900 dark:text-surface-50 tabular-nums leading-none">
                    {{ stat.value }}
                  </p>
                </div>
                <div
                  v-if="stat.icon"
                  class="ml-3 flex-shrink-0 p-2 rounded-xl transition-all duration-300 group-hover:scale-110"
                  :style="{ backgroundColor: stat.color + '14' }"
                >
                  <component :is="stat.icon" class="h-5 w-5" :style="{ color: stat.color }" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Charts -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-up" style="animation-delay: 140ms">
          <BaseCard class="analytics-card">
            <template #header>
              <div class="flex items-center gap-3">
                <div class="p-1.5 rounded-lg bg-accent-500/10">
                  <ChartBarIcon class="h-5 w-5 text-accent-500" />
                </div>
                <div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-50">
                    Volume by {{ userProfile?.group ? 'Vessel' : 'Asset' }}
                  </h3>
                  <p class="text-[10px] text-surface-400 dark:text-surface-500">Fuel consumption distribution across fleet</p>
                </div>
              </div>
            </template>
            <div class="h-80">
              <BarChart :chart-data="chartData" :chart-options="horizontalBarOptions" :key="chartKey" />
            </div>
          </BaseCard>

          <BaseCard class="analytics-card">
            <template #header>
              <div class="flex items-center gap-3">
                <div class="p-1.5 rounded-lg bg-accent-500/10">
                  <CalendarIcon class="h-5 w-5 text-accent-500" />
                </div>
                <div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-50">Monthly Trend</h3>
                  <p class="text-[10px] text-surface-400 dark:text-surface-500">Volume and CO2 savings over time</p>
                </div>
              </div>
            </template>
            <div class="h-80">
              <BarChart :chart-data="chartData2" :chart-options="monthlyBarOptions" :key="chartKey" />
            </div>
          </BaseCard>
        </div>

        <!-- Transaction Table -->
        <div class="animate-fade-up" style="animation-delay: 200ms">
          <div class="flex items-center gap-2 mb-4">
            <div class="h-px flex-1 bg-gradient-to-r from-primary-500/40 to-transparent"></div>
            <span class="text-xs font-bold uppercase tracking-[0.2em] text-primary-500 dark:text-primary-300">All Transactions</span>
            <div class="h-px flex-1 bg-gradient-to-l from-primary-500/40 to-transparent"></div>
          </div>

          <BaseCard class="analytics-card">
            <template #header>
              <div class="flex items-center gap-3">
                <div class="p-1.5 rounded-lg bg-primary-500/10">
                  <TableCellsIcon class="h-5 w-5 text-primary-500" />
                </div>
                <div>
                  <h3 class="text-sm font-bold text-surface-900 dark:text-surface-50">Transaction Details</h3>
                  <p class="text-[10px] text-surface-400 dark:text-surface-500">{{ formatNumber(transactions.length) }} records found</p>
                </div>
              </div>
            </template>

            <DataTable
              v-if="transactions.length"
              :headers="headers"
              :items="transactions"
              :search-placeholder="`Search transactions`"
              :rows-per-page="25"
              :export-filename="`transactions-${startDate}-${endDate}.csv`"
            >
              <template #item-transaction_datetime="{ transaction_datetime }">
                <span class="text-surface-600 dark:text-surface-300 font-mono text-xs tabular-nums">
                  {{ formatDate(transaction_datetime) }}
                </span>
              </template>

              <template #item-site_name="{ site_name }">
                <span
                  v-if="site_name"
                  class="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-surface-100 text-surface-700 dark:bg-surface-800 dark:text-surface-200 border border-surface-200/60 dark:border-surface-700/60"
                >
                  {{ site_name }}
                </span>
                <span v-else class="text-surface-400">—</span>
              </template>

              <template #item-odometer="{ odometer }">
                <span class="font-mono text-xs tabular-nums text-surface-700 dark:text-surface-300">
                  {{ odometer != null ? formatNumberFull(odometer) : '—' }}
                </span>
              </template>

              <template #item-quantity="{ quantity }">
                <span class="font-semibold font-mono text-surface-900 dark:text-surface-50 tabular-nums">
                  {{ formatNumberFull(quantity) }} L
                </span>
              </template>

              <template #item-product_name="{ product_name }">
                <span class="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide bg-accent-50 text-accent-700 dark:bg-accent-900/30 dark:text-accent-300 border border-accent-200/50 dark:border-accent-700/30">
                  {{ product_name }}
                </span>
              </template>

              <template #item-co2_saved="{ quantity }">
                <span class="font-mono text-xs tabular-nums text-emerald-600 dark:text-emerald-400 font-semibold">
                  {{ formatNumberFull(quantity * co2FactorFor()) }} Kg
                </span>
              </template>

              <template #empty>
                <EmptyState
                  :icon="DocumentTextIcon"
                  title="No transactions found"
                  message="There are no transactions for the selected period. Try adjusting your date range or filters."
                  variant="info"
                />
              </template>
            </DataTable>

            <div v-else class="py-12">
              <EmptyState
                :icon="DocumentTextIcon"
                title="No transactions found"
                message="There are no transactions for the selected period. Try adjusting your date range or filters."
                variant="info"
              />
            </div>
          </BaseCard>
        </div>
      </div>
    </main>

    <Footer />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { formatInTimeZone } from 'date-fns-tz'
import { authFetch } from '@/composables/useApi'
import { savingsFactorFor } from '@/utils/emissions'

import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import DateRangePicker from '@/components/dashboard/DateRangePicker.vue'
import EntityFilter from '@/components/dashboard/EntityFilter.vue'
import BarChart from '@/components/BarChart.vue'
import DataTable from '@/components/ui/DataTable.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import PortalAdminCustomerPicker from '@/components/PortalAdminCustomerPicker.vue'
import { useEffectiveCustomers } from '@/composables/useEffectiveCustomers'
import { useCustomerDataRange } from '@/composables/useCustomerDataRange'

import {
  ArrowLeftIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CalendarIcon,
  TableCellsIcon,
  AdjustmentsHorizontalIcon,
  TruckIcon,
  BeakerIcon,
  CloudIcon,
} from '@heroicons/vue/24/outline'

const store = useStore()
const route = useRoute()
const router = useRouter()

const customer_id = route.params.customer_id
const transactions = ref([])

const TZ = 'Asia/Dubai'
const dateRange = ref({
  startDate: formatInTimeZone(
    new Date(new Date().getTime() - 180 * 24 * 3600 * 1000),
    TZ,
    'yyyy-MM-dd'
  ),
  endDate: formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd')
})
const chartKey = ref(0)

const userProfile = computed(() => store.state.userProfile)
const { isPortalSuperAdmin, customersMcs } = useEffectiveCustomers()
const startDate = computed(() => dateRange.value.startDate)
const endDate = computed(() => dateRange.value.endDate)

const { minDate, maxDate } = useCustomerDataRange(computed(() => [Number(customer_id)]))

const selectedEntities = ref([])
const allEntities = ref([])
const selectedSites = ref([])
const allSites = ref([])

// Per-customer CO2-savings factor (kg saved / litre). This view is scoped to a
// single customer (route param customer_id), so default to that customer.
const co2FactorFor = (mcsCustomerId) =>
  savingsFactorFor(mcsCustomerId ?? customer_id, customersMcs.value)

const chartColors = {
  accent: '#10B981',
  primary: '#003A6D',
  palette: ['#10B981', '#0891B2', '#003A6D', '#7C3AED', '#F59E0B', '#F43F5E', '#0D9488', '#8B5CF6', '#EC4899', '#06B6D4']
}

// Quick Stats
const quickStats = computed(() => {
  if (!transactions.value.length) return []

  const totalTransactions = transactions.value.length
  const totalVolume = transactions.value.reduce((sum, t) => sum + t.quantity, 0)
  const uniqueEntities = new Set(transactions.value.map(t => t.vehicle_no)).size
  const totalCo2 = totalVolume * co2FactorFor()

  return [
    {
      label: 'Total Transactions',
      value: formatNumber(totalTransactions),
      icon: DocumentTextIcon,
      color: '#10B981'
    },
    {
      label: userProfile.value?.group ? 'Active Vessels' : 'Active Assets',
      value: uniqueEntities.toString(),
      icon: TruckIcon,
      color: '#059669'
    },
    {
      label: 'Total Volume',
      value: `${formatCompact(totalVolume)} L`,
      icon: BeakerIcon,
      color: '#0D9488'
    },
    {
      label: 'CO2 Saved',
      value: `${formatCompact(totalCo2)} Kg`,
      icon: CloudIcon,
      color: '#047857'
    }
  ]
})

const headers = computed(() => [
  { value: 'transaction_datetime', text: 'Sale Date', sortable: true },
  { value: 'site_name', text: 'Site', sortable: true },
  {
    value: 'vehicle_no',
    text: userProfile.value?.group ? 'Vessel Name' : 'Asset Number',
    sortable: true,
  },
  {
    value: 'odometer',
    text: userProfile.value?.group ? 'Engine Hours' : 'Odometer Reading',
    sortable: true,
  },
  { value: 'quantity', text: 'Volume (L)', sortable: true },
  { value: 'product_name', text: 'Fuel Type', sortable: true },
  { value: 'co2_saved', text: 'CO2 Saved (Kg)', sortable: true },
])

const horizontalBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y',
  plugins: { legend: { display: false } },
  scales: {
    x: { beginAtZero: true, grid: { drawBorder: false } },
    y: { grid: { display: false } },
  },
}

const monthlyBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { padding: 16, usePointStyle: true, pointStyleWidth: 8 } },
  },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true, grid: { drawBorder: false } },
  },
}

const formatDate = (isoDate) => {
  const date = new Date(isoDate)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatNumber = (num) => {
  if (typeof num !== 'number' || isNaN(num)) return '0'
  return Math.round(num).toLocaleString()
}

const formatNumberFull = (num) => {
  return num?.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

const formatCompact = (num) => {
  if (typeof num !== 'number' || isNaN(num)) return '0'
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
  return num.toFixed(0)
}

const fetchTransactions = async () => {
  const apiUrl = store.state.apiUrl

  try {
    const response = await authFetch(
      `${apiUrl}api/v1/customers/${customer_id}/transactions?start_date=${startDate.value}&end_date=${endDate.value}`,
      {},
      store.state.user
    )

    if (response.ok) {
      const result = await response.json()
      let fetchedTransactions = result.data

      const entitiesSet = new Set()
      const sitesSet = new Set()
      fetchedTransactions.forEach((t) => {
        entitiesSet.add(t.vehicle_no)
        if (t.site_name) sitesSet.add(t.site_name)
      })
      allEntities.value = Array.from(entitiesSet).map((name) => ({ name }))
      allSites.value = Array.from(sitesSet).sort().map((name) => ({ name }))

      if (selectedEntities.value.length > 0) {
        fetchedTransactions = fetchedTransactions.filter((t) =>
          selectedEntities.value.map((e) => e.name).includes(t.vehicle_no)
        )
      }

      if (selectedSites.value.length > 0) {
        const chosen = selectedSites.value.map((s) => s.name)
        fetchedTransactions = fetchedTransactions.filter((t) => chosen.includes(t.site_name))
      }

      transactions.value = fetchedTransactions
      updateCharts()
    } else {
      console.error('Error fetching transactions')
    }
  } catch (error) {
    console.error('Error:', error)
  }
}

const chartData = ref({ labels: [], datasets: [] })
const chartData2 = ref({ labels: [], datasets: [] })

const updateCharts = () => {
  // Chart 1: Volume by Entity (horizontal bar, top 15)
  const entityMap = {}
  transactions.value.forEach((t) => {
    entityMap[t.vehicle_no] = (entityMap[t.vehicle_no] || 0) + t.quantity
  })

  const sorted = Object.entries(entityMap).sort((a, b) => b[1] - a[1]).slice(0, 15)

  chartData.value = {
    labels: sorted.map(e => e[0]),
    datasets: [{
      label: 'Fuel Volume (L)',
      data: sorted.map(e => e[1]),
      backgroundColor: sorted.map((_, i) => chartColors.palette[i % chartColors.palette.length] + 'CC'),
      borderColor: sorted.map((_, i) => chartColors.palette[i % chartColors.palette.length]),
      borderWidth: 1,
      borderRadius: 4,
    }]
  }

  // Chart 2: Monthly Volume + CO2
  const monthVolumeMap = {}
  const monthCo2Map = {}
  transactions.value.forEach((t) => {
    const date = new Date(t.transaction_datetime)
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    monthVolumeMap[key] = (monthVolumeMap[key] || 0) + t.quantity
    monthCo2Map[key] = (monthCo2Map[key] || 0) + (t.quantity * co2FactorFor())
  })

  const sortedMonths = Object.keys(monthVolumeMap).sort()
  const monthLabels = sortedMonths.map(m => {
    const [year, month] = m.split('-')
    return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
  })

  chartData2.value = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Volume (L)',
        data: sortedMonths.map(m => monthVolumeMap[m]),
        backgroundColor: chartColors.accent + 'CC',
        borderColor: chartColors.accent,
        borderWidth: 1,
        borderRadius: 6,
      },
      {
        label: 'CO2 Saved (Kg)',
        data: sortedMonths.map(m => monthCo2Map[m]),
        backgroundColor: chartColors.primary + '80',
        borderColor: chartColors.primary,
        borderWidth: 1,
        borderRadius: 6,
      }
    ]
  }

  chartKey.value++
}

// Portal super admins browse via the store selection, but this view is driven
// by the route param. If they ever disagree (stale history entry, back/forward
// navigation after switching customers), the banner and CO2 factors would
// describe a different customer than the rendered data — bail out to the
// dashboard instead of showing a mismatched view.
const portalAdminRouteMismatch = () => {
  if (!isPortalSuperAdmin.value) return false
  const selectedId = store.state.portalAdminCustomer?.mcs_customer_id
  return Number(selectedId) !== Number(route.params.customer_id)
}

if (portalAdminRouteMismatch()) {
  router.replace('/dashboard')
} else {
  fetchTransactions()
}

watch([isPortalSuperAdmin, () => route.params.customer_id], () => {
  if (portalAdminRouteMismatch()) router.replace('/dashboard')
})

watch([startDate, endDate, selectedEntities, selectedSites], () => {
  fetchTransactions()
})
</script>

<style scoped>
.kpi-card {
  @apply relative overflow-hidden bg-white dark:bg-surface-900/80 rounded-2xl p-4 border border-surface-200/60 dark:border-surface-700/60;
  @apply hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300;
  animation: fadeUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

.analytics-card {
  @apply rounded-2xl border border-surface-200/60 dark:border-surface-700/60 bg-white dark:bg-surface-900/80;
}

.animate-fade-up {
  animation-fill-mode: backwards;
}

button {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
